import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { TaskProps, TaskStatus, Severity, Priority } from "../types/interfaces";
import { taskKeys, sprintKeys } from "../utils/queryKeys";

export interface TaskFilters {
  projectId?: string;
  status?: TaskStatus;
  sprintId?: string;
  epicId?: string;
  assigneeId?: string;
  severity?: Severity;
  priority?: Priority;
  // only tasks not in any sprint (the backlog)
  backlog?: boolean;
  sort?: "rank" | "importance";
  enabled?: boolean;
}

function toQueryString(filters: TaskFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (key === "enabled") return;
    if (value) params.set(key, String(value));
  });
  return params.toString();
}

// `projectId` is optional: without it the list spans every project the caller can see.
export function useTasks(filters: TaskFilters = {}) {
  const { enabled, ...keyFilters } = filters;
  return useQuery({
    queryKey: taskKeys.list(Object.fromEntries(Object.entries(keyFilters).map(([k, v]) => [k, v === undefined ? undefined : String(v)]))),
    queryFn: async () => (await apiRequest<TaskProps[]>(`tasks?${toQueryString(filters)}`)).data,
    enabled: enabled ?? true,
  });
}

export function useCreateTask(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<TaskProps> & { title: string }) =>
      (await apiRequest<TaskProps>("tasks", {
        method: "POST",
        body: JSON.stringify({ projectId, ...data }),
      })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: sprintKeys.all });
    },
  });
}

export function useUpdateTask(_projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TaskProps> }) =>
      (await apiRequest<TaskProps>(`tasks/${id}`, { method: "PUT", body: JSON.stringify(data) })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: sprintKeys.all });
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
    },
  });
}

export function useTransitionTaskStatus(_projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TaskStatus }) =>
      (await apiRequest<TaskProps>(`tasks/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: sprintKeys.all });
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
    },
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: async () => (await apiRequest<TaskProps>(`tasks/${id}`)).data,
    enabled: !!id,
  });
}
